import { useEffect, useMemo, useState } from "react";
import { BaseRepository } from "./base-repository";
import { sql } from "@/domains/database/utils";

export type SmartViewRow = {
  id: string;
  associated_space_account_id: string;
  smart_context_id: string;
  derived_kind: string | null;
  derived_internal_label: string | null;
  value_json: unknown;
  has_found_more: boolean;
  refreshed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export class SmartViewRepository extends BaseRepository {
  async upsertView(params: SmartViewRow): Promise<void> {
    const {
      id,
      associated_space_account_id,
      smart_context_id,
      derived_kind = null,
      derived_internal_label = null,
      value_json,
      has_found_more = false,
      refreshed_at = null,
      created_at = new Date().toISOString(),
      updated_at = new Date().toISOString(),
    } = params;

    await this.db.query(
      sql`
        INSERT INTO smart_views (
          id,
          associated_space_account_id,
          smart_context_id,
          derived_kind,
          derived_internal_label,
          value_json,
          has_found_more,
          refreshed_at,
          created_at,
          updated_at
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)
        ON CONFLICT (id) DO UPDATE SET
          associated_space_account_id = EXCLUDED.associated_space_account_id,
          smart_context_id = EXCLUDED.smart_context_id,
          derived_kind = EXCLUDED.derived_kind,
          derived_internal_label = EXCLUDED.derived_internal_label,
          value_json = EXCLUDED.value_json,
          has_found_more = EXCLUDED.has_found_more,
          refreshed_at = EXCLUDED.refreshed_at,
          updated_at = EXCLUDED.updated_at
      `,
      [
        id,
        associated_space_account_id,
        smart_context_id,
        derived_kind,
        derived_internal_label,
        JSON.stringify(value_json),
        has_found_more,
        refreshed_at,
        created_at,
        updated_at,
      ]
    );
  }

  useLatestByContextId({ smartContextId }: { smartContextId: string | null }): {
    view: SmartViewRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const [view, setView] = useState<SmartViewRow | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const query = useMemo(
      () =>
        sql`
          SELECT *
          FROM smart_views
          WHERE smart_context_id = $1
          ORDER BY refreshed_at DESC NULLS LAST,
                   updated_at DESC NULLS LAST,
                   created_at DESC NULLS LAST
          LIMIT 1
        `,
      []
    );

    const isReady = this.connectionManager.isReady();

    useEffect(() => {
      if (!smartContextId) {
        setView(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      if (!isReady) {
        return;
      }

      const controller = new AbortController();
      const db = this.getDbIfReady();
      if (!db) {
        return () => {
          controller.abort();
        };
      }
      let cleanup: (() => void) | undefined;

      db.live
        .query<SmartViewRow>({ query, params: [smartContextId], signal: controller.signal })
        .then(({ initialResults, subscribe, unsubscribe }) => {
          const apply = (rows: SmartViewRow[]) => {
            setView(rows[0] ?? null);
            setIsLoading(false);
          };

          apply(initialResults.rows as SmartViewRow[]);

          const onUpdate = (results: { rows: SmartViewRow[] }) => apply(results.rows);
          subscribe(onUpdate);
          cleanup = () => void unsubscribe(onUpdate);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          setError(err?.message ?? "Unknown error");
          setIsLoading(false);
        });

      return () => {
        controller.abort();
        if (cleanup) cleanup();
      };
    }, [query, smartContextId, isReady]);

    return { view, isLoading, error };
  }

  useById({ smartViewId }: { smartViewId: string | null }): {
    view: SmartViewRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const [view, setView] = useState<SmartViewRow | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const query = useMemo(
      () =>
        sql`
          SELECT *
          FROM smart_views
          WHERE id = $1
          LIMIT 1
        `,
      []
    );

    const isReady = this.connectionManager.isReady();

    useEffect(() => {
      if (!smartViewId) {
        setView(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      if (!isReady) {
        return;
      }

      const controller = new AbortController();
      const db = this.getDbIfReady();
      if (!db) {
        return () => {
          controller.abort();
        };
      }
      let cleanup: (() => void) | undefined;

      db.live
        .query<SmartViewRow>({ query, params: [smartViewId], signal: controller.signal })
        .then(({ initialResults, subscribe, unsubscribe }) => {
          const apply = (rows: SmartViewRow[]) => {
            setView(rows[0] ?? null);
            setIsLoading(false);
          };

          apply(initialResults.rows as SmartViewRow[]);

          const onUpdate = (results: { rows: SmartViewRow[] }) => apply(results.rows);
          subscribe(onUpdate);
          cleanup = () => void unsubscribe(onUpdate);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          setError(err?.message ?? "Unknown error");
          setIsLoading(false);
        });

      return () => {
        controller.abort();
        if (cleanup) cleanup();
      };
    }, [query, smartViewId, isReady]);

    return { view, isLoading, error };
  }
}
